import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Search, TestTube, Box, RefreshCw, GitBranch, Users, FlaskConical, Play } from 'lucide-react';
import '../styles/sections.css';
import './Segp.css';
import Footer from '../components/Footer.jsx';

const phases = [
  {
    number: '01',
    title: 'Security',
    text: 'Scan for 7 risk indicators and run an 8-step review checklist before any code is trusted.',
    icon: <Shield size={20} />,
  },
  {
    number: '02',
    title: 'Static Analysis',
    text: 'Validate spec compliance, score descriptions, detect No-Ops, and enforce the anti-bloat rule.',
    icon: <Search size={20} />,
  },
  {
    number: '03',
    title: 'Evaluation',
    text: '5-5-5 test case mix, deterministic Regex checks, LLM-as-a-Judge, and Skill Lift ablation.',
    icon: <TestTube size={20} />,
  },
  {
    number: '04',
    title: 'Isolation',
    text: 'Modal Sandboxes and Hermes profiles prevent context bleeding and test across harnesses.',
    icon: <Box size={20} />,
  },
  {
    number: '05',
    title: 'Lifecycle',
    text: 'DRAFT → REVIEW → TEST → STAGING → PRODUCTION → MONITOR → DEPRECATE → RETIRED.',
    icon: <RefreshCw size={20} />,
  },
  {
    number: '06',
    title: 'CI/CD',
    text: '6-stage pipeline with GitHub Actions, SHA-256 version pinning, and rollback support.',
    icon: <GitBranch size={20} />,
  },
  {
    number: '07',
    title: 'Governance',
    text: 'Skill registry, role-based bundles with recall limits, and a consolidation advisor.',
    icon: <Users size={20} />,
  },
];

function VideoPlayer({ src, ariaLabel }) {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);

  const handleEnded = () => {
    setEnded(true);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setEnded(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="segpHeroVideo"
    >
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={handleEnded}
        aria-label={ariaLabel}
      />
      {ended && (
        <button
          className="segpReplayButton"
          onClick={handleReplay}
          aria-label={`Replay ${ariaLabel}`}
        >
          <Play size={16} />
          Replay
        </button>
      )}
    </motion.div>
  );
}

function VisualGuideVideo() {
  const videoRef = useRef(null);
  const [ended, setEnded] = useState(false);

  const handleEnded = () => {
    setEnded(true);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setEnded(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.8 }}
      className="visualGuideVideo"
    >
      <video
        ref={videoRef}
        src="/videos/segp-concepts.mp4"
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={handleEnded}
        aria-label="SEGP visual guide: concepts that power the platform"
      />
      {ended && (
        <button
          className="segpReplayButton"
          onClick={handleReplay}
          aria-label="Replay SEGP visual guide video"
        >
          <Play size={16} />
          Replay
        </button>
      )}
    </motion.div>
  );
}

export default function Segp() {
  return (
    <>
      <section className="segpHero">
        <div className="container">
          <VideoPlayer
            src="/videos/segp-hero.mp4"
            ariaLabel="Keraunos SEGP glass thunderbolt concentric circles animation"
          />

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="segpHeroTitle"
          >
            Keraunos SEGP
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="segpHeroSubtitle"
          >
            Skill Evaluation & Governance Platform — a 7-phase, CI/CD-integrated
            pipeline that makes AI agent skills safe, measurable, and
            production-ready before they ever reach production.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="badgeRow"
          >
            <span className="badge"><FlaskConical size={14} /> 152 tests</span>
            <span className="badge">Pydantic v2 models</span>
            <span className="badge">Typer CLI</span>
            <span className="badge">Modal Sandboxes</span>
            <span className="badge">Hermes Goals integration</span>
          </motion.div>
        </div>
      </section>

      <section className="phaseSection">
        <div className="container">
          <div className="sectionHeader">
            <span className="sectionLabel">The 7 Phases</span>
            <h2 className="sectionTitle">From first scan to production governance.</h2>
          </div>

          <div className="phaseGrid">
            {phases.map((phase, idx) => (
              <motion.div
                key={phase.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.45, delay: idx * 0.07 }}
                className="phaseCard glassCard"
              >
                <div className="phaseNumber">{phase.number}</div>
                <h3>{phase.title}</h3>
                <p>{phase.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="imageGallery sectionAlt">
        <div className="container">
          <div className="sectionHeader">
            <span className="sectionLabel">Visual Guide</span>
            <h2 className="sectionTitle">Concepts that power the platform.</h2>
          </div>

          <VisualGuideVideo />
        </div>
      </section>

      <section className="ctaBanner">
        <div className="container">
          <h2>Proprietary governance for agent skills.</h2>
          <p>
            SEGP is part of the Keraunos platform. It brings deterministic
            evaluation, lifecycle management, and governance to AI agent skills
          </p>
          <a href="/" className="btnPrimary">
            Back to Keraunos AI
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
}
